import { parseRoleName } from "./parseRoleName";
import { BY_ROLE_PL, GENERIC_ACTION_PL } from "./companyUserRolesRoleHelp.pl";

/** @typedef {{ includes: string, adminNote: string }} RoleHelpBlock */

const GENERIC_ACTION_KEYS = new Set([
  "read",
  "update",
  "create",
  "delete",
  "approve",
  "reject",
]);

const GENERIC_ACTION = {
  pl: GENERIC_ACTION_PL,
  nb: {
    read: {
      includes: "Lesetilgang til innhold som rollen dekker innenfor aktivert modul.",
      adminNote:
        "Brukeren kan se andres data (kunder, prosjekter, rapporter osv.). Vurder behov for innsyn og interne retningslinjer for personvern.",
    },
    create: {
      includes: "Kan opprette nye poster (oppdrag, timer, dokumenter osv.) innenfor modulen.",
      adminNote:
        "Sørg for at maler og obligatoriske felt er satt opp slik at nye registreringer blir konsistente og komplette.",
    },
    update: {
      includes: "Kan endre eksisterende poster innenfor det rollen tillater.",
      adminNote:
        "Endringer kan påvirke økonomi, HMS og kundebilde. Begrens rollen til de som faktisk skal kunne rette feil.",
    },
    delete: {
      includes: "Kan slette eller arkivere innhold der systemet tillater det.",
      adminNote:
        "Sletting kan være irreversibel eller påvirke sporbarhet. Kun for pålitelige brukere med tydelig ansvar.",
    },
    approve: {
      includes: "Kan godkjenne (f.eks. timer, dokumenter, bestillinger) der arbeidsflyten krever det.",
      adminNote:
        "Godkjenning låser ofte poster for andre. Fordel ansvaret slik at det alltid finnes backup-approvere.",
    },
    reject: {
      includes: "Kan avvise eller sende i retur poster som krever behandling.",
      adminNote:
        "Avvisning påvirker andre brukere og frister. Sørg for at mottaker får tydelig beskjed (varsling/arbeidsflyt).",
    },
  },
  en: {
    read: {
      includes: "Read access to data covered by this role within the enabled module.",
      adminNote:
        "The user may see colleagues’ data (customers, projects, reports, etc.). Consider privacy and internal access policy.",
    },
    create: {
      includes: "Can create new records (jobs, hours, documents, etc.) in the module.",
      adminNote:
        "Ensure templates and required fields are configured so new entries stay consistent and complete.",
    },
    update: {
      includes: "Can change existing records allowed by this role.",
      adminNote:
        "Edits can affect finance, HSE and customer-facing outputs. Limit this role to people who should correct data.",
    },
    delete: {
      includes: "Can delete or archive content where the system allows.",
      adminNote:
        "Deletion may be permanent or affect audit trails. Assign only to trusted users with clear accountability.",
    },
    approve: {
      includes: "Can approve items (e.g. hours, documents, orders) where the workflow requires it.",
      adminNote:
        "Approval often locks records for others. Ensure more than one approver where continuity matters.",
    },
    reject: {
      includes: "Can reject or send back items pending review.",
      adminNote:
        "Rejections affect other users and deadlines. Make sure recipients get clear feedback (notifications/workflow).",
    },
  },
  sv: {
    read: {
      includes: "Läsrättigheter till innehåll som rollen omfattar inom aktiverad modul.",
      adminNote:
        "Användaren kan se andras data (kunder, projekt, rapporter m.m.). Överväg integritet och intern åtkomstpolicy.",
    },
    create: {
      includes: "Kan skapa nya poster (uppdrag, timmar, dokument m.m.) i modulen.",
      adminNote:
        "Säkerställ att mallar och obligatoriska fält är uppsatta så att nya registreringar blir enhetliga och fullständiga.",
    },
    update: {
      includes: "Kan ändra befintliga poster inom vad rollen tillåter.",
      adminNote:
        "Ändringar kan påverka ekonomi, arbetsmiljö och kundbild. Begränsa rollen till dem som ska kunna rätta fel.",
    },
    delete: {
      includes: "Kan radera eller arkivera innehåll där systemet tillåter det.",
      adminNote:
        "Radering kan vara oåterkallelig eller påverka spårbarhet. Endast för pålitliga användare med tydligt ansvar.",
    },
    approve: {
      includes: "Kan godkänna (t.ex. timmar, dokument, order) där arbetsflödet kräver det.",
      adminNote:
        "Godkännande låser ofta poster för andra. Fördela ansvaret så att det finns reservgodkännare.",
    },
    reject: {
      includes: "Kan avvisa eller skicka tillbaka poster som väntar på hantering.",
      adminNote:
        "Avvisning påverkar andra användare och deadlines. Säkerställ tydlig återkoppling (avisering/flöde).",
    },
  },
};

/** @type {Record<string, Record<"nb"|"en"|"sv", RoleHelpBlock>>} */
const BY_ROLE = {
  admin: {
    nb: {
      includes:
        "Full administrativ tilgang i Promang for selskapet: brukere, roller, innstillinger og alle moduler som er aktivert for selskapet.",
      adminNote:
        "Gi kun til pålitelige personer. Admin ser sensitiv bedriftsinformasjon og kan endre tilganger for alle andre. Aktiver kun moduler selskapet faktisk bruker.",
    },
    en: {
      includes:
        "Full administrative access for the company in Promang: users, roles, settings and every module enabled for the company.",
      adminNote:
        "Assign only to trusted people. Admins see sensitive business data and can change everyone else’s access. Enable only modules the company actually uses.",
    },
    sv: {
      includes:
        "Full administrativ åtkomst i Promang för företaget: användare, roller, inställningar och alla moduler som är aktiverade.",
      adminNote:
        "Ge endast till pålitliga personer. Admin ser känslig företagsinformation och kan ändra alla andras behörigheter. Aktivera bara moduler företaget verkligen använder.",
    },
  },
  phone: {
    nb: {
      includes: "Tilgang til telefoni/kommunikasjon mot kunder der Phonero eller tilsvarende er konfigurert.",
      adminNote:
        "Krever gyldige integrasjonsbrukere/passord på selskapet. Begrens til de som skal håndtere inn/utgående samtaler.",
    },
    en: {
      includes: "Access to telephony/customer communication when Phonero (or similar) is configured.",
      adminNote:
        "Requires valid integration credentials on the company. Limit to staff who should handle inbound/outbound calls.",
    },
    sv: {
      includes: "Åtkomst till telefoni/kundkommunikation när Phonero (eller motsvarande) är konfigurerat.",
      adminNote:
        "Kräver giltiga integrationsuppgifter på företaget. Begränsa till personal som ska hantera samtal.",
    },
  },
  "phone_control-panel": {
    nb: {
      includes: "Bruk av sentralbord/kontrollpanel for samtaler og køer.",
      adminNote: "Kombiner med telefonimodul og opplæring i rutiner for viderekobling og logging.",
    },
    en: {
      includes: "Use of control panel / switchboard for calls and queues.",
      adminNote: "Combine with the phone module and training on transfer and logging routines.",
    },
    sv: {
      includes: "Användning av växel/kontrollpanel för samtal och köer.",
      adminNote: "Kombinera med telefonimodul och utbildning i vidarekopplings- och loggningsrutiner.",
    },
  },
  phone_settings: {
    nb: {
      includes: "Kan endre telefoni-innstillinger for selskapet.",
      adminNote: "Feil innstillinger kan stoppe samtaler eller påvirke kundeopplevelse. Få personer bør ha denne rollen.",
    },
    en: {
      includes: "Can change telephony settings for the company.",
      adminNote: "Misconfiguration can break calls or hurt customer experience. Keep this role to very few users.",
    },
    sv: {
      includes: "Kan ändra telefoniinställningar för företaget.",
      adminNote: "Fel inställningar kan störa samtal eller kundupplevelse. Få personer bör ha rollen.",
    },
  },
  calendar: {
    nb: {
      includes: "Se og bruke firmakalender (avtaler, deltakere, egen deltakelse).",
      adminNote:
        "Kalenderen kan avsløre kunde- og prosjektaktivitet. Vurder hvem som trenger full kalenderoversikt.",
    },
    en: {
      includes: "View and use the company calendar (appointments, participants, own participation).",
      adminNote:
        "The calendar can reveal customer and project activity. Consider who needs a full overview.",
    },
    sv: {
      includes: "Visa och använda företagskalendern (möten, deltagare, egen medverkan).",
      adminNote:
        "Kalendern kan visa kund- och projektaktivitet. Överväg vem som behöver full överblick.",
    },
  },
  customers: {
    nb: {
      includes: "Arbeid med kundekort: kontakter, adresser, avtaler og relatert grunninformasjon.",
      adminNote:
        "Kundedata er ofte personvernregulert. Gi kun til salg, service og ledelse som trenger det i jobben.",
    },
    en: {
      includes: "Work with customer records: contacts, addresses, agreements and related master data.",
      adminNote:
        "Customer data is often privacy-sensitive. Limit to sales, service and roles that genuinely need it.",
    },
    sv: {
      includes: "Arbete med kundkort: kontakter, adresser, avtal och grundläggande information.",
      adminNote:
        "Kunddata är ofta integritetskänslig. Ge endast till sälj, service och roller som verkligen behöver det.",
    },
  },
  "customer-handler": {
    nb: {
      includes: "Utføre kundebehandlingsoppgaver i Promang (oppfølging, registrering).",
      adminNote: "Avklar ansvar mot «customers»-rollen slik at ikke alle får full redigering uten behov.",
    },
    en: {
      includes: "Perform customer-handling tasks in Promang (follow-up, registration).",
      adminNote: "Clarify overlap with the main customers role so not everyone gets full edit access without need.",
    },
    sv: {
      includes: "Utföra kundhanteringsuppgifter i Promang (uppföljning, registrering).",
      adminNote: "Klargör ansvar gentemot huvudrollen «customers» så att inte alla får full redigeringsrätt i onödan.",
    },
  },
  customer_settings: {
    nb: {
      includes: "Endre innstillinger for kundemodulen (felter, rutiner, tilpasninger).",
      adminNote: "Endringer gjelder hele selskapet. Test i testmiljø eller begrensede brukere før bred utrulling.",
    },
    en: {
      includes: "Change settings for the customer module (fields, routines, customisations).",
      adminNote: "Changes apply company-wide. Prefer testing or a small pilot group before wide rollout.",
    },
    sv: {
      includes: "Ändra inställningar för kundmodulen (fält, rutiner, anpassningar).",
      adminNote: "Ändringar gäller hela företaget. Testa gärna i begränsad grupp innan bred utrullning.",
    },
  },
  contacts: {
    nb: {
      includes: "Tilgang til kontakter knyttet til kunder eller prosjekter.",
      adminNote: "Persondata i kontakter – følg GDPR og interne lister over hvem som kan eksportere/lage uttrekk.",
    },
    en: {
      includes: "Access to contacts linked to customers or projects.",
      adminNote: "Contacts hold personal data—follow GDPR and internal rules on export and extracts.",
    },
    sv: {
      includes: "Åtkomst till kontakter kopplade till kunder eller projekt.",
      adminNote: "Kontakter innehåller personuppgifter—följ GDPR och interna regler för export och uttag.",
    },
  },
  projects: {
    nb: {
      includes: "Se prosjekter, mapper, deltakere og grunnleggende prosjektinformasjon.",
      adminNote:
        "Kombiner med «projects_edit» eller prosjektroller der du vil at brukeren skal kunne endre struktur og økonomi.",
    },
    en: {
      includes: "View projects, folders, participants and basic project information.",
      adminNote:
        "Combine with edit roles or project roles where the user should change structure and financials.",
    },
    sv: {
      includes: "Visa projekt, mappar, deltagare och grundläggande projektinformation.",
      adminNote:
        "Kombinera med redigeringsroller eller projektroller när användaren ska kunna ändra struktur och ekonomi.",
    },
  },
  projects_edit: {
    nb: {
      includes: "Redigere prosjekter (navn, status, økonomi der systemet tillater).",
      adminNote: "Unngå at for mange kan endre budsjett og nøkkeldata – bruk prosjektansvarlig som mønster.",
    },
    en: {
      includes: "Edit projects (name, status, budget where the system allows).",
      adminNote: "Avoid broad edit rights on budget and key fields—align with project owner practices.",
    },
    sv: {
      includes: "Redigera projekt (namn, status, budget där systemet tillåter).",
      adminNote: "Undvik att för många kan ändra budget och nyckeldata—följ projektägarens praxis.",
    },
  },
  projects_create: {
    nb: {
      includes: "Opprette nye prosjekter.",
      adminNote: "Sørg for at prosjektmaler og nummerering er avtalt slik at nye prosjekter blir enhetlige.",
    },
    en: {
      includes: "Create new projects.",
      adminNote: "Agree templates and numbering so new projects stay consistent.",
    },
    sv: {
      includes: "Skapa nya projekt.",
      adminNote: "Säkerställ mallar och numrering så att nya projekt blir enhetliga.",
    },
  },
  projects_plan: {
    nb: {
      includes:
        "Prosjektplan (tidslinje), prosjekthendelser og API for plan — for prosjekter du er deltaker på. Viser under «Prosjekter»-menyen uten full «projects»-rolle.",
      adminNote:
        "Krever prosjektdeltakelse (project access). Gir ikke arbeidsordrer, mapper eller generell prosjektredigering — bruk «projects» / «projects_edit» ved behov.",
    },
    en: {
      includes:
        "Project plan timeline, project events, and plan API for projects you are a participant on. Appears under Projects without the full projects role.",
      adminNote:
        "Requires project participation (project access). Does not grant work orders, folders, or general project editing—assign projects / projects_edit as needed.",
    },
    sv: {
      includes:
        "Projektplan (tidslinje), projekthändelser och plan-API för projekt du deltar i. Visas under Projekt utan full «projects»-roll.",
      adminNote:
        "Kräver projektdeltagande (project access). Ger inte arbetsorder, mappar eller allmän projektredigering—lägg till projects / projects_edit vid behov.",
    },
  },
  projects_accounting: {
    nb: {
      includes:
        "Fakturaer, leverandørfakturaer, faktureringsplan og summering på prosjekter du er deltaker på (egen fane på prosjektsiden).",
      adminNote:
        "Krever prosjektdeltakelse. Redigeringsknapper i disse fanene krever i tillegg prosjektredigering (eller admin); ellers vises data som skrivebeskyttet.",
    },
    en: {
      includes:
        "Customer invoices, supplier invoices, billing plan, and project summary on projects you participate in (dedicated tabs on the project page).",
      adminNote:
        "Requires project participation. Edit controls in those tabs also require project edit permission (or admin); otherwise the UI is read-only.",
    },
    sv: {
      includes:
        "Kundfakturor, leverantörsfakturor, faktureringsplan och projektsammanfattning för projekt du deltar i (egna flikar på projektsidan).",
      adminNote:
        "Kräver projektdeltagande. Redigeringsknappar kräver dessutom projektredigeringsbehörighet (eller admin); annars visas skrivskyddat.",
    },
  },
  project_settings: {
    nb: {
      includes: "Endre prosjektmodulens innstillinger for selskapet.",
      adminNote: "Påvirker alle prosjekter. Begrens til administrator eller prosjektledelse.",
    },
    en: {
      includes: "Change project module settings for the company.",
      adminNote: "Affects all projects. Keep to admins or project leadership.",
    },
    sv: {
      includes: "Ändra projektmodulens inställningar för företaget.",
      adminNote: "Påverkar alla projekt. Begränsa till admin eller projektledning.",
    },
  },
  "projects_work-orders": {
    nb: {
      includes: "Se arbeidsordrer brukeren er involvert i (deltaker/rolle).",
      adminNote:
        "For redigering og utskrift trengs ofte «projects_work-orders_edit». Sjekk at riktige personer er satt som deltakere.",
    },
    en: {
      includes: "View work orders the user participates in (participant role).",
      adminNote:
        "Editing and printing usually need «projects_work-orders_edit». Ensure the right people are participants.",
    },
    sv: {
      includes: "Visa arbetsorder som användaren deltar i (deltagarroll).",
      adminNote:
        "Redigering och utskrift kräver ofta «projects_work-orders_edit». Säkerställ rätt deltagare.",
    },
  },
  "projects_work-orders_edit": {
    nb: {
      includes: "Redigere og skrive ut arbeidsordrer der brukeren er deltaker.",
      adminNote: "Arbeidsordrer kan inneholde priser og kundedetaljer – begrens til fagpersonell.",
    },
    en: {
      includes: "Edit and print work orders where the user is a participant.",
      adminNote: "Work orders may contain pricing and customer details—limit to appropriate staff.",
    },
    sv: {
      includes: "Redigera och skriva ut arbetsorder där användaren är deltagare.",
      adminNote: "Arbetsorder kan innehålla priser och kunddetaljer—begränsa till rätt personal.",
    },
  },
  "projects_work-orders_create": {
    nb: {
      includes: "Opprette nye arbeidsordrer i prosjekter brukeren har tilgang til.",
      adminNote: "Avklar hvem som skal kunne opprette nye AO mot fakturerings- og godkjenningsrutiner.",
    },
    en: {
      includes: "Create new work orders in projects the user can access.",
      adminNote: "Clarify who may create WOs versus billing and approval routines.",
    },
    sv: {
      includes: "Skapa nya arbetsorder i projekt användaren har åtkomst till.",
      adminNote: "Klargör vem som får skapa AO gentemot fakturerings- och godkännanderutiner.",
    },
  },
  hours: {
    nb: {
      includes: "Registrere og se egne timer (og eventuelt andres der rollen tillater).",
      adminNote:
        "Kombiner med «hours_approval» eller «edit_hours» for godkjenning og full redigering. Sett tydelige frister for godkjenning.",
    },
    en: {
      includes: "Register and view own hours (and others’ where the role allows).",
      adminNote:
        "Combine with approval or edit-hours roles for processing. Set clear approval deadlines.",
    },
    sv: {
      includes: "Registrera och visa egna timmar (och andras där rollen tillåter).",
      adminNote:
        "Kombinera med godkännande- eller redigeringsroller för hantering. Sätt tydliga deadlines.",
    },
  },
  hours_register: {
    nb: {
      includes: "Registrere fravær/permisjon for egen bruker der fraværsmodulen er aktiv.",
      adminNote: "Synlighet mot leder/lønn bør avklares. Sørg for riktige fraværstyper i oppsettet.",
    },
    en: {
      includes: "Register own absences/leave when the absences module is active.",
      adminNote: "Clarify visibility to managers/payroll. Keep absence types correctly configured.",
    },
    sv: {
      includes: "Registrera egen frånvaro/ledighet när frånvaromodulen är aktiv.",
      adminNote: "Klargör synlighet för chef/lön. Håll frånvarotyper korrekt konfigurerade.",
    },
  },
  hours_approval: {
    nb: {
      includes: "Se timer som krever oppfølging i godkjenningsflyt (innenfor modulens regler).",
      adminNote:
        "Lønn og fakturering bygger ofte på godkjente timer. Fordel approvere og ha varselrutiner ved ferie.",
    },
    en: {
      includes: "View hours that need follow-up in the approval flow (within module rules).",
      adminNote:
        "Payroll and billing often depend on approved hours. Distribute approvers and plan for holidays.",
    },
    sv: {
      includes: "Visa timmar som kräver uppföljning i godkännandeflödet (inom modulens regler).",
      adminNote:
        "Lön och fakturering bygger ofta på godkända timmar. Fördela godkännare och planera för semester.",
    },
  },
  hours_overview: {
    nb: {
      includes:
        "Selskapsdekkende timeoversikt (alle prosjekt i perioden) med filter på aktivitet, prosjekt, ansatte og avdeling, pluss estimert kostnad og salg.",
      adminNote:
        "Krever timeregistrering (hours) i tillegg. Gir ikke godkjenning eller fakturering alene. Ikke bare admin.",
    },
    en: {
      includes:
        "Company-wide hours overview (all projects in the period) with activity, project, employee and department filters, plus estimated cost and sales.",
      adminNote:
        "Combine with time registration (hours). Does not grant approval or billing by itself. Not admin-only.",
    },
    sv: {
      includes:
        "Företagsövergripande tidsöversikt (alla projekt i perioden) med filter för aktivitet, projekt, anställda och avdelning, plus uppskattad kostnad och försäljning.",
      adminNote:
        "Kräver tidsregistrering (hours) utöver rollen. Ger inte godkännande eller fakturering ensam.",
    },
  },
  hours_monthly_overview: {
    nb: {
      includes: "Månedlig timeoversikt for timeansvarlig (selskapets «hours handler»).",
      adminNote: "Knyttes til feltet timeansvarlig på selskapet – verifiser at riktig person er valgt.",
    },
    en: {
      includes: "Monthly hours overview for the company’s designated hours handler.",
      adminNote: "Tied to the hours-handler field on the company—verify the correct person is set.",
    },
    sv: {
      includes: "Månadsöversikt för timmar för företagets timansvarige.",
      adminNote: "Kopplas till fältet för timansvarig på företaget—kontrollera rätt person.",
    },
  },
  hour_settings: {
    nb: {
      includes: "Endre timeinnstillinger (koder, regler, integrasjoner mot lønn).",
      adminNote: "Feil oppsett gir feil lønnsgrunnlag. Begrens til HR/lønn og administrator.",
    },
    en: {
      includes: "Change hour settings (codes, rules, payroll integrations).",
      adminNote: "Misconfiguration affects payroll. Limit to HR/payroll and admins.",
    },
    sv: {
      includes: "Ändra timinställningar (koder, regler, löneintegrationer).",
      adminNote: "Fel upplägg påverkar löneunderlag. Begränsa till HR/lön och admin.",
    },
  },
  edit_hours: {
    nb: {
      includes: "Redigere andres timer (eksport, korrigering) der time-modulen er aktiv.",
      adminNote: "Sterk tilgang mot lønnsunderlag. Loggfør rutiner og begrens til få superbrukere.",
    },
    en: {
      includes: "Edit others’ hours (export, corrections) when the hours module is active.",
      adminNote: "Strong access to payroll data. Keep to very few trusted users with audit discipline.",
    },
    sv: {
      includes: "Redigera andras timmar (export, korrigering) när timmodulen är aktiv.",
      adminNote: "Stark åtkomst till löneunderlag. Begränsa till få superanvändare med spårbar rutin.",
    },
  },
  service: {
    nb: {
      includes: "Service og oppdrag: kunder, rapporter, dokumenter, SMS/varsling iht. tilganger.",
      adminNote:
        "Ofte bred rolle. Suppler med mer spesifikke roller (avtaler, rapporter) der du vil begrense hva feltpersonell ser.",
    },
    en: {
      includes: "Service work: customers, reports, documents, SMS/notifications per granted access.",
      adminNote:
        "Often broad—add narrower roles (agreements, reports) if field staff should see less.",
    },
    sv: {
      includes: "Service: kunder, rapporter, dokument, SMS/aviseringar enligt behörighet.",
      adminNote:
        "Ofta bred—lägg till smalare roller (avtal, rapporter) om fältpersonal ska se mindre.",
    },
  },
  service_reports: {
    nb: {
      includes: "Opprette, oppdatere og følge opp servicerapporter.",
      adminNote: "Rapporter kan inneholde kundedata og bilder – følg lagring og bildepolicy.",
    },
    en: {
      includes: "Create, update and follow up service reports.",
      adminNote: "Reports may hold customer data and photos—follow storage and media policy.",
    },
    sv: {
      includes: "Skapa, uppdatera och följa upp servicerapporter.",
      adminNote: "Rapporter kan innehålla kunddata och bilder—följ lagrings- och mediapolicy.",
    },
  },
  service_agreements: {
    nb: {
      includes: "Arbeide med serviceavtaler og tilhørende planer.",
      adminNote: "Avtaler binder økonomi og forpliktelser – hvem som kan endre pris og vilkår bør være begrenset.",
    },
    en: {
      includes: "Work with service agreements and related schedules.",
      adminNote: "Agreements bind economics—limit who can change pricing and terms.",
    },
    sv: {
      includes: "Arbeta med serviceavtal och tillhörande planer.",
      adminNote: "Avtal binder ekonomi—begränsa vem som får ändra pris och villkor.",
    },
  },
  service_settings: {
    nb: {
      includes: "Endre serviceinnstillinger (maler, felt, flyt).",
      adminNote: "Påvirker alle brukere som rapporterer service. Endre i samråd med kvalitetsansvarlig.",
    },
    en: {
      includes: "Change service settings (templates, fields, flows).",
      adminNote: "Affects everyone reporting service—coordinate with quality/service leads.",
    },
    sv: {
      includes: "Ändra serviceinställningar (mallar, fält, flöden).",
      adminNote: "Påverkar alla som rapporterar service—samordna med kvalitet/serviceansvarig.",
    },
  },
  quality: {
    nb: {
      includes:
        "HMS/KS-relatert innhold: dokumenter, rutiner, org.kart, kjemikalier m.m. (innenfor aktivert «quality»-modul).",
      adminNote:
        "Mange funksjoner er underlagt revisjon og ansvar. Fordel «manage»-roller til kvalitetsleder/HMS-koordinator.",
    },
    en: {
      includes:
        "HSE/quality content: documents, procedures, org charts, chemicals, etc. (within the enabled quality module).",
      adminNote:
        "Much of this is audit-sensitive. Assign manage-type roles to your HSE/quality coordinator.",
    },
    sv: {
      includes:
        "HMS/kvalitetsinnehåll: dokument, rutiner, org.scheman, kemikalier m.m. (inom aktiverad kvalitetsmodul).",
      adminNote:
        "Mycket är revisionskänsligt. Ge hanteringsroller till HMS/kvalitetssamordnare.",
    },
  },
  quality_checklists: {
    nb: {
      includes: "Sjekklister: utføre, følge opp og avslutte sjekkpunkter.",
      adminNote: "Sørg for at ansvarlig utfører er satt og at avvik meldes inn i avviksmodulen ved behov.",
    },
    en: {
      includes: "Checklists: execute, follow up and complete checkpoints.",
      adminNote: "Ensure responsible executors are set and deviations go to the deviation workflow when needed.",
    },
    sv: {
      includes: "Checklistor: genomföra, följa upp och slutföra kontrollpunkter.",
      adminNote: "Säkerställ ansvarig utförare och att avvikelser följer avvikelseflödet vid behov.",
    },
  },
  quality_deviations: {
    nb: {
      includes: "Registrere og følge opp avvik og tiltak.",
      adminNote: "Avvik kan være sensitive. Begrens hvem som kan lukke eller slette avvik.",
    },
    en: {
      includes: "Register and follow up deviations and actions.",
      adminNote: "Deviations can be sensitive. Limit who can close or delete them.",
    },
    sv: {
      includes: "Registrera och följa upp avvikelser och åtgärder.",
      adminNote: "Avvikelser kan vara känsliga. Begränsa vem som får stänga eller radera.",
    },
  },
  quality_settings: {
    nb: {
      includes: "Endre innstillinger for HMS/KS-dokumenter og tilknyttede typer.",
      adminNote: "Endringer påvirker klassifisering og godkjenning. Kun HMS/KS-ansvarlig eller admin.",
    },
    en: {
      includes: "Change settings for HSE/quality documents and related types.",
      adminNote: "Changes affect classification and approval—HSE/quality lead or admin only.",
    },
    sv: {
      includes: "Ändra inställningar för HMS/kvalitetsdokument och relaterade typer.",
      adminNote: "Påverkar klassning och godkännande—endast HMS/kvalitetsansvarig eller admin.",
    },
  },
  hse: {
    nb: {
      includes: "Lesetilgang til utvalgte HMS-dokumenter og rutiner (eldre/forenklet HMS-rolle).",
      adminNote: "Kombiner med «quality» der du trenger bredere dokumenttilgang.",
    },
    en: {
      includes: "Read access to selected HSE documents and procedures (legacy/simplified HSE role).",
      adminNote: "Combine with the quality module role where broader document access is needed.",
    },
    sv: {
      includes: "Läsrätt till utvalda HMS-dokument och rutiner (förenklad HMS-roll).",
      adminNote: "Kombinera med kvalitetsrollen där bredare dokumentåtkomst behövs.",
    },
  },
  hse_qualities: {
    nb: {
      includes: "Arbeide med kvalitetsmål/-poster i HMS/KS-delen.",
      adminNote: "Avklar hvem som eier oppdatering mot revisjon og ledelsens gjennomgang.",
    },
    en: {
      includes: "Work with quality records in the HSE/quality area.",
      adminNote: "Clarify ownership for updates versus audits and management review.",
    },
    sv: {
      includes: "Arbeta med kvalitetsposter inom HMS/kvalitet.",
      adminNote: "Klargör ansvar för uppdatering gentemot revision och ledningens genomgång.",
    },
  },
  hse_instructions: {
    nb: {
      includes: "Tilgang til arbeidsinstrukser og rutiner.",
      adminNote: "Instrukser skal være gjeldende versjon – begrens hvem som kan publisere endringer.",
    },
    en: {
      includes: "Access to work instructions and procedures.",
      adminNote: "Instructions must stay current—limit who can publish changes.",
    },
    sv: {
      includes: "Åtkomst till arbetsinstruktioner och rutiner.",
      adminNote: "Instruktioner ska vara aktuella—begränsa vem som får publicera ändringar.",
    },
  },
  hse_qualities_manage: {
    nb: {
      includes: "Opprette, endre og administrere kvalitetsposter.",
      adminNote: "Sterk rolle mot revisjonsspor – kun HMS/KS-ansvarlig eller utpekt person.",
    },
    en: {
      includes: "Create, change and administer quality records.",
      adminNote: "Strong impact on audit trails—HSE/quality lead or designated person only.",
    },
    sv: {
      includes: "Skapa, ändra och administrera kvalitetsposter.",
      adminNote: "Stark påverkan på revisionsspår—endast HMS/kvalitetsansvarig eller utsedd person.",
    },
  },
  hse_instructions_manage: {
    nb: {
      includes: "Publisere og administrere arbeidsinstrukser.",
      adminNote: "Feil instruks kan gi HMS-brudd. Bruk godkjenningsflyt og versjonering.",
    },
    en: {
      includes: "Publish and administer work instructions.",
      adminNote: "Wrong instructions create HSE risk—use approval flow and versioning.",
    },
    sv: {
      includes: "Publicera och administrera arbetsinstruktioner.",
      adminNote: "Fel instruktion skapar arbetsmiljorisk—använd godkännandeflöde och versionering.",
    },
  },
  chemical_inventory: {
    nb: {
      includes: "Kjemikaliekartotek: stoffer, HMS-nivåer og relatert dokumentasjon.",
      adminNote: "Krever fagkompetanse (KAM/sikkerhetsdatablad). Oppdater ved innkjøp og utfasing.",
    },
    en: {
      includes: "Chemical inventory: substances, HSE levels and related documentation.",
      adminNote: "Requires competent ownership (SDS/COSHH). Update on procurement and phase-out.",
    },
    sv: {
      includes: "Kemikalieförteckning: ämnen, risknivåer och relaterad dokumentation.",
      adminNote: "Kräver kompetens (säkerhetsdatablad). Uppdatera vid inköp och utfasning.",
    },
  },
  hse_ks: {
    nb: {
      includes: "HMS/KS-modulen: hendelser, risiko, dokumenter, kompetanse og evaluering (etter aktive delmoduler).",
      adminNote:
        "Modulen er ofte ISO-relevant. Sett eier for dokumenter, risiko og kompetansekart – og hvem som får «manage»-roller.",
    },
    en: {
      includes: "HSE/KS module: events, risk, documents, competency and evaluation (per enabled sub-features).",
      adminNote:
        "Often ISO-relevant. Assign owners for documents, risk maps and competency—and restrict manage roles.",
    },
    sv: {
      includes: "HMS/KS-modulen: händelser, risk, dokument, kompetens och utvärdering (per aktiverade delmoduler).",
      adminNote:
        "Ofta ISO-relevant. Utse ansvariga för dokument, risk och kompetens—och begränsa hanteringsroller.",
    },
  },
  hse_ks_events: {
    nb: {
      includes: "Registrere og følge opp HMS-hendelser (nestenulykker, avvik, observasjoner).",
      adminNote: "Sørg for at alvorlighetsgrad og eskalering er avtalt med ledergruppe.",
    },
    en: {
      includes: "Register and follow up HSE events (near misses, observations).",
      adminNote: "Agree severity levels and escalation with management.",
    },
    sv: {
      includes: "Registrera och följa upp HMS-händelser (tillbud, observationer).",
      adminNote: "Enas om allvarlighetsgrad och eskalering med ledningen.",
    },
  },
  hse_ks_risk: {
    nb: {
      includes: "Risikovurderinger og risikoregister.",
      adminNote: "Risiko skal vedlikeholdes når prosesser endres. Unngå «spøkelses»-vurderinger uten eier.",
    },
    en: {
      includes: "Risk assessments and risk registers.",
      adminNote: "Refresh risk when processes change. Avoid ownerless assessments.",
    },
    sv: {
      includes: "Riskbedömningar och riskregister.",
      adminNote: "Uppdatera risk när processer ändras. Undvik riskbedömningar utan ansvarig.",
    },
  },
  hse_ks_documents: {
    nb: {
      includes: "Lese og bruke HMS/KS-dokumenter og revisjoner.",
      adminNote: "Lesere bør ikke automatisk få godkjennerrett – skill «approve»-rollen.",
    },
    en: {
      includes: "Read and use HSE/KS documents and revisions.",
      adminNote: "Readers should not automatically be approvers—separate the approve role.",
    },
    sv: {
      includes: "Läsa och använda HMS/KS-dokument och revisioner.",
      adminNote: "Läsgodkänn inte automatiskt utan separat godkännarroll.",
    },
  },
  hse_ks_documents_approve: {
    nb: {
      includes: "Godkjenne dokument og revisjoner i HMS/KS-flyten.",
      adminNote: "Kun personer med faglig myndighet. Loggfør hvem som godkjenner hva (ansvar/kompetanse).",
    },
    en: {
      includes: "Approve documents and revisions in the HSE/KS workflow.",
      adminNote: "Only people with formal authority. Keep clear accountability for each approval.",
    },
    sv: {
      includes: "Godkänna dokument och revisioner i HMS/KS-flödet.",
      adminNote: "Endast personer med formell behörighet. Tydlig ansvarsfördelning per godkännande.",
    },
  },
  hse_ks_competency: {
    nb: {
      includes: "Se kompetansekrav, opplæring og oppfølging for roller.",
      adminNote: "Personopplysninger i opplæringshistorikk – følg personvern og innsynsbegrensning.",
    },
    en: {
      includes: "View competency requirements, training and follow-up for roles.",
      adminNote: "Training history can be personal data—respect privacy and access limits.",
    },
    sv: {
      includes: "Se kompetenskrav, utbildning och uppföljning för roller.",
      adminNote: "Utbildningshistorik kan vara personuppgifter—följ integritet och begränsad åtkomst.",
    },
  },
  hse_ks_competency_manage: {
    nb: {
      includes: "Administrere kompetansematrise, roller og krav.",
      adminNote: "Endringer påvirker hvem som får utføre arbeid. Koordiner med HR og verneombud.",
    },
    en: {
      includes: "Administer competency matrices, roles and requirements.",
      adminNote: "Changes affect who may perform work—coordinate with HR and safety reps.",
    },
    sv: {
      includes: "Administrera kompetensmatris, roller och krav.",
      adminNote: "Ändringar påverkar vem som får utföra arbete—samordna med HR och skyddsombud.",
    },
  },
  hse_ks_evaluation: {
    nb: {
      includes: "Vernerunder, evalueringer og oppfølgingspunkter i HMS/KS.",
      adminNote: "Resultater kan kreve tiltak – avklar ansvarlig for oppfølging innen frist.",
    },
    en: {
      includes: "Safety rounds, evaluations and follow-up items in HSE/KS.",
      adminNote: "Outcomes may require actions—assign clear owners and deadlines.",
    },
    sv: {
      includes: "Skyddsronder, utvärderingar och uppföljningspunkter inom HMS/KS.",
      adminNote: "Utfall kan kräva åtgärder—utse ansvariga och deadlines.",
    },
  },
  hse_ks_settings: {
    nb: {
      includes: "Endre innstillinger for HMS/KS-modulen.",
      adminNote: "Kun administrator eller HMS/KS-leder. Feil felt påvirker rapportering til ledelsen.",
    },
    en: {
      includes: "Change settings for the HSE/KS module.",
      adminNote: "Admin or HSE/QS lead only—wrong fields affect management reporting.",
    },
    sv: {
      includes: "Ändra inställningar för HMS/KS-modulen.",
      adminNote: "Endast admin eller HMS/KS-ledare—fel fält påverkar ledningsrapportering.",
    },
  },
  products: {
    nb: {
      includes: "Produktkatalog: artikler, priser, lagerrelatert visning der modulen tillater.",
      adminNote: "Prisendringer slår ut i tilbud og ordre – begrens til innkjøp/salg.",
    },
    en: {
      includes: "Product catalogue: items, prices, stock-related views where allowed.",
      adminNote: "Price changes flow to quotes and orders—limit to purchasing/sales roles.",
    },
    sv: {
      includes: "Produktkatalog: artiklar, priser, lagerrelaterad vy där modulen tillåter.",
      adminNote: "Prisändringar påverkar offerter och order—begränsa till inköp/försäljning.",
    },
  },
  product_settings: {
    nb: {
      includes: "Endre produktinnstillinger og strukturer.",
      adminNote: "Påvirker hele salgskjeden. Test endringer før de settes i produksjon.",
    },
    en: {
      includes: "Change product settings and structures.",
      adminNote: "Affects the whole sales chain—test before production rollout.",
    },
    sv: {
      includes: "Ändra produktinställningar och strukturer.",
      adminNote: "Påverkar hela säljkedjan—testa innan produktionssättning.",
    },
  },
  orders: {
    nb: {
      includes: "Ordrehåndtering: opprette, endre status og følge ordrelinjer.",
      adminNote: "Ordre binder lager og faktura. Avklar hvem som kan kansellere eller endre pris.",
    },
    en: {
      includes: "Order handling: create, change status and follow order lines.",
      adminNote: "Orders tie to stock and invoicing—clarify who may cancel or change price.",
    },
    sv: {
      includes: "Orderhantering: skapa, ändra status och följa orderrader.",
      adminNote: "Order kopplas till lager och faktura—klargör vem som får annullera eller ändra pris.",
    },
  },
  order_settings: {
    nb: {
      includes: "Endre ordre- og salgsinnstillinger.",
      adminNote: "Kun få brukere. Feil innstillinger gir feil MVA og integrasjoner.",
    },
    en: {
      includes: "Change order and sales settings.",
      adminNote: "Very few users—misconfiguration breaks VAT and integrations.",
    },
    sv: {
      includes: "Ändra order- och försäljningsinställningar.",
      adminNote: "Få användare—fel inställningar påverkar moms och integrationer.",
    },
  },
  offers: {
    nb: {
      includes: "Tilbud: utarbeide, sende og følge opp tilbud mot kunde.",
      adminNote: "Tilbud kan binde pris – avklar rabattgrenser og godkjenning av avvik.",
    },
    en: {
      includes: "Offers: prepare, send and follow up quotes to customers.",
      adminNote: "Quotes can bind pricing—set discount limits and approval for exceptions.",
    },
    sv: {
      includes: "Offerter: ta fram, skicka och följa upp mot kund.",
      adminNote: "Offerter kan binda pris—sätt rabattgränser och godkännande för undantag.",
    },
  },
  offers_settings: {
    nb: {
      includes: "Endre tilbudsinnstillinger og maler.",
      adminNote: "Malendringer påvirker alle selgere – versjoner og kommuniser før aktivering.",
    },
    en: {
      includes: "Change offer settings and templates.",
      adminNote: "Template changes hit all sales users—version and communicate before go-live.",
    },
    sv: {
      includes: "Ändra offertinställningar och mallar.",
      adminNote: "Malländringar påverkar alla säljare—versionera och informera före aktivering.",
    },
  },
  inventory: {
    nb: {
      includes: "Lager: beholdning, lokasjoner, flyttinger innenfor modulen.",
      adminNote: "Fysisk og systembeholdning må stemme – bruk tellinger og avstemming regelmessig.",
    },
    en: {
      includes: "Inventory: stock, locations, moves within the module.",
      adminNote: "Physical and system stock must align—use counts and reconciliation regularly.",
    },
    sv: {
      includes: "Lager: saldo, platser, flyttningar inom modulen.",
      adminNote: "Fysiskt och systemmässigt saldo ska stämma—använd inventering regelbundet.",
    },
  },
  inventory_settings: {
    nb: {
      includes: "Endre lageroppsett (lokasjoner, integrasjoner, regler).",
      adminNote: "Feil lokasjon gir plukkefeil. Dokumenter endringer for drift.",
    },
    en: {
      includes: "Change inventory setup (locations, integrations, rules).",
      adminNote: "Wrong locations cause picking errors—document changes for operations.",
    },
    sv: {
      includes: "Ändra lagerupplägg (platser, integrationer, regler).",
      adminNote: "Fel plats ger plockfel—dokumentera ändringar för drift.",
    },
  },
  inventory_finance: {
    nb: {
      includes: "Se kostpriser, lagerverdi og perioderapporter for lageret.",
      adminNote: "Gir innsyn i økonomiske lagerdata. Tildel bare ansatte som trenger avstemming.",
    },
    en: {
      includes: "View inventory costs, valuation and period reports.",
      adminNote: "Grants access to financial inventory data. Assign only to staff who reconcile stock.",
    },
    sv: {
      includes: "Se lagerkostnader, lagervärde och periodrapporter.",
      adminNote: "Ger åtkomst till ekonomiska lageruppgifter. Tilldela bara personal som stämmer av lagret.",
    },
  },
  counts: {
    nb: {
      includes: "Tellelister og varetelling i lager.",
      adminNote: "Telling påvirker bokført beholdning – planlegg kutt-off og ansvarlig teller.",
    },
    en: {
      includes: "Count sheets and stock counts.",
      adminNote: "Counts affect booked stock—plan cut-off and accountable counters.",
    },
    sv: {
      includes: "Inventeringslistor och räkningar.",
      adminNote: "Räkning påverkar bokfört saldo—planera cut-off och ansvarig personal.",
    },
  },
  cases: {
    nb: {
      includes: "Se alle saker i firmaet. Uten denne rollen kan du likevel åpne saker du er utførende på eller nevnt i.",
      adminNote: "Tildel til de som trenger oversikt. Opprette, skrive og redigere er egne roller.",
    },
    en: {
      includes: "See all cases in the company. Without this role you can still open cases you are assigned on or mentioned in.",
      adminNote: "Grant to staff who need the full list. Create, write and edit are separate roles.",
    },
    sv: {
      includes: "Se alla ärenden i företaget. Utan denna roll kan du ändå öppna ärenden du är utförande på eller nämnd i.",
      adminNote: "Tilldela till dem som behöver översikten. Skapa, skriva och redigera är egna roller.",
    },
  },
  cases_create: {
    nb: {
      includes: "Opprette nye saker.",
      adminNote: "Gir ikke automatisk liste over alle saker.",
    },
    en: {
      includes: "Create new cases.",
      adminNote: "Does not grant a company-wide case list.",
    },
    sv: {
      includes: "Skapa nya ärenden.",
      adminNote: "Ger inte automatiskt lista över alla ärenden.",
    },
  },
  cases_write: {
    nb: {
      includes: "Skrive oppdateringer på saker du kan lese.",
      adminNote: "Krever at saken er synlig (alle saker, utførende eller omtale).",
    },
    en: {
      includes: "Write updates on cases you can read.",
      adminNote: "Requires the case to be visible (all cases, assignee, or mention).",
    },
    sv: {
      includes: "Skriva uppdateringar på ärenden du kan läsa.",
      adminNote: "Kräver att ärendet är synligt (alla ärenden, utförande eller omnämnande).",
    },
  },
  cases_edit: {
    nb: {
      includes: "Redigere saksfelter, overføre og slette. Inkluderer også skriving og lesing av alle saker.",
      adminNote: "Sterk rettighet – begrens til saksansvarlige.",
    },
    en: {
      includes: "Edit case fields, transfer and delete. Also includes writing and reading all cases.",
      adminNote: "Strong permission—limit to case owners.",
    },
    sv: {
      includes: "Redigera ärendefält, överföra och radera. Inkluderar också skrivning och läsning av alla ärenden.",
      adminNote: "Stark behörighet—begränsa till ärendeansvariga.",
    },
  },
  driving: {
    nb: {
      includes:
        "Uten «driving_trips»: full flåte (alle turer, alle kjøretøy i API, Sjåfører-meny sammen med «driving_drivers»). Med «driving_trips» samtidig: foreldrerolle for Kjøring-meny, men du er **sjåfør** — kun egne turer og innlogget kjøretøy, ingen full **Vehicles**-liste.",
      adminNote:
        "Ren flåtesjef: «driving» uten «driving_trips». Sjåfør som trenger modul-meny: «driving» + «driving_trips»; full bilpark krever «driving_vehicles» eller fjern «driving_trips».",
    },
    en: {
      includes:
        "Without driving_trips: full fleet (all trips and vehicles in the API, Drivers menu with driving_drivers). With driving_trips at the same time: parent access to the Driving area, but you are a **driver** — own trips and checked-in vehicle only; no full **Vehicles** list.",
      adminNote:
        "Pure fleet manager: driving without driving_trips. Driver who needs the menu: driving + driving_trips; for the full vehicle list add driving_vehicles or remove driving_trips.",
    },
    sv: {
      includes:
        "Utan driving_trips: hela flottan (alla resor och fordon i API, Förare-meny med driving_drivers). Med driving_trips samtidigt: överordnad Kör-meny men du är **förare** — egna resor och incheckat fordon; ingen full **Vehicles**-lista.",
      adminNote:
        "Ren flåtchef: driving utan driving_trips. Förare som behöver menyn: driving + driving_trips; full fordonslista kräver driving_vehicles eller ta bort driving_trips.",
    },
  },
  driving_trips: {
    nb: {
      includes:
        "Egne turer, innlogget kjøretøy og håndboklenke. Sammen med «driving» (uten «driving_vehicles») er du fortsatt sjåfør — ikke alle firmature og ikke **Vehicles**-listen.",
      adminNote:
        "Krever Driver-rad. Full flåte: «driving» uten denne rollen, eller «driving_drivers» / «driving_vehicles» etter behov.",
    },
    en: {
      includes:
        "Own trips, checked-in vehicle, handbook link. Together with driving (without driving_vehicles) you stay a **driver** — not all company trips and no **Vehicles** list.",
      adminNote:
        "Requires a Driver row. For full fleet access use driving without this role, or assign driving_drivers / driving_vehicles as needed.",
    },
    sv: {
      includes:
        "Egna resor, incheckat fordon, handbokslänk. Tillsammans med driving (utan driving_vehicles) förblir du **förare** — inte alla företagsresor och ingen **Vehicles**-lista.",
      adminNote:
        "Kräver Driver-post. Full flotta: driving utan denna roll, eller driving_drivers / driving_vehicles vid behov.",
    },
  },
  driving_drivers: {
    nb: {
      includes:
        "Menypunktet Sjåfører og **alle** firmature i API (sammen med admin) for oppfølging; full **Vehicles**-liste i frontend.",
      adminNote:
        "Førerkort og helseopplysninger – begrens innsyn. Ren sjåfør uten flåte: «driving» + «driving_trips» (evt. «vehicles»), uten «driving_drivers» og uten «driving_vehicles».",
    },
    en: {
      includes:
        "Drivers menu and **full** company trip list in the API (with admin) for oversight; full **Vehicles** list in the app.",
      adminNote:
        "Licence and health data—limit visibility. Pure driver: driving + driving_trips (optionally vehicles), without driving_drivers and without driving_vehicles.",
    },
    sv: {
      includes:
        "Menyn Förare och **hela** företagets reslista i API (med admin); full **Vehicles**-lista i appen.",
      adminNote:
        "Körkort och hälsodata—begränsa åtkomst. Ren förare: driving + driving_trips (valfritt vehicles), utan driving_drivers och utan driving_vehicles.",
    },
  },
  driving_vehicles: {
    nb: {
      includes:
        "Alle selskapets kjøretøy (liste, detaljer, EU-kontroll, dokumenter, håndbok). Kan velges uten «driving»; gir ikke tilgang til alle firmature (kun «driving»/admin/sjåførregister).",
      adminNote:
        "Kombinert med kun «driving_trips»: full kjøretøyliste, men turene er fortsatt filtrert til sjåførens egne turer.",
    },
    en: {
      includes:
        "All company vehicles (list, details, inspections, documents, handbook). Can be assigned without driving; it does not unlock the full company trip list (only driving / admin / driver roster).",
      adminNote:
        "Combined with driving_trips only: full vehicle list, but trips stay filtered to that driver’s own trips.",
    },
    sv: {
      includes:
        "Alla företagets fordon (lista, detaljer, besiktning, dokument, handbok). Kan tilldelas utan driving; ger inte hela företagets reslista (endast driving / admin / förarregister).",
      adminNote:
        "Tillsammans med bara driving_trips: full fordonslista, men resor filtreras fortfarande till förarens egna resor.",
    },
  },
  vehicles: {
    nb: {
      includes:
        "Eldre støtterolle for menyer; i API gir den ikke full kjøretøyliste alene — samme som sjåfør: innlogget kjøretøy når sjåførpost finnes. Full flåte: «driving_vehicles» eller «driving»/sjåførregister.",
      adminNote:
        "Ikke bruk «vehicles» som erstatning for «driving_vehicles» når brukeren skal se hele bilparken.",
    },
    en: {
      includes:
        "Legacy support role for menus; alone it does not unlock the full vehicle list in the API — same as a driver: checked-in vehicle when a driver record exists. Full fleet: driving_vehicles or driving / driver roster.",
      adminNote:
        "Do not rely on vehicles instead of driving_vehicles when the user must manage the whole fleet list.",
    },
    sv: {
      includes:
        "Legacy-stödroll för menyer; ger inte full fordonslista i API för sig — som förare: incheckat fordon om förarpost finns. Hela flottan: driving_vehicles eller driving / förarregister.",
      adminNote:
        "Använd inte vehicles som ersättning för driving_vehicles när användaren ska se hela fordonslistan.",
    },
  },
  tools: {
    nb: {
      includes: "Verktøymodulen: utstyr som følges opp i drift.",
      adminNote: "Koble til ansvarlig for kontroll og påsyn av verktøy som krever dokumentasjon.",
    },
    en: {
      includes: "Tools module: equipment tracked in operations.",
      adminNote: "Assign owners for inspections and statutory tool documentation.",
    },
    sv: {
      includes: "Verktygsmodulen: utrustning som följs upp i drift.",
      adminNote: "Koppla ansvarig för kontroll och dokumentation enligt krav.",
    },
  },
  resource_hub: {
    nb: {
      includes: "Ressurssenter: opplæring, FAQ og delt støttemateriell.",
      adminNote: "Hold innhold oppdatert – utdaterte artikler skaper supportstøy.",
    },
    en: {
      includes: "Resource hub: training, FAQ and shared support material.",
      adminNote: "Keep content fresh—stale articles create noise for support.",
    },
    sv: {
      includes: "Resurscenter: utbildning, FAQ och delat stödmaterial.",
      adminNote: "Håll innehåll aktuellt—inaktuella artiklar skapar onödig support.",
    },
  },
  faq: {
    nb: {
      includes: "Administrere FAQ og hjelpeartikler i ressurssenteret.",
      adminNote: "Endringer synes for alle – korrekt språk og gyldige lenker er viktig.",
    },
    en: {
      includes: "Administer FAQ and help articles in the resource hub.",
      adminNote: "Changes are company-wide—keep language correct and links valid.",
    },
    sv: {
      includes: "Administrera FAQ och hjälpartiklar i resurscentret.",
      adminNote: "Ändringar syns för alla—korrekt språk och giltiga länkar är viktigt.",
    },
  },
  templates: {
    nb: {
      includes: "Dokumentmaler og standardtekster brukt i Promang.",
      adminNote: "Malfeil forplanter seg til mange brukere – test med én brukergruppe først.",
    },
    en: {
      includes: "Document templates and standard texts used in Promang.",
      adminNote: "Template bugs propagate—pilot with one user group first.",
    },
    sv: {
      includes: "Dokumentmallar och standardtexter i Promang.",
      adminNote: "Mallfel sprids brett—pilottesta med en användargrupp först.",
    },
  },
  website: {
    nb: {
      includes: "Firmawebsider eller landingssider hostet/koblet via Promang.",
      adminNote: "Publisert innhold er offentlig – godkjenn tekst og bilder før publisering.",
    },
    en: {
      includes: "Company web pages or landing pages linked via Promang.",
      adminNote: "Published content is public—approve copy and imagery before go-live.",
    },
    sv: {
      includes: "Företagswebbsidor eller landningssidor via Promang.",
      adminNote: "Publicerat innehåll är offentligt—godkänn text och bilder före publicering.",
    },
  },
  webpage: {
    nb: {
      includes: "Redigere enkeltsider i nettstedsmodulen.",
      adminNote: "SEO, personvern og tilgjengelighet – følg mal og juridisk gjennomgang.",
    },
    en: {
      includes: "Edit individual pages in the website module.",
      adminNote: "SEO, privacy and accessibility—follow template and legal review.",
    },
    sv: {
      includes: "Redigera enskilda sidor i webbmodulen.",
      adminNote: "SEO, integritet och tillgänglighet—följ mall och juridisk granskning.",
    },
  },
  absences: {
    nb: {
      includes: "Oversikt over fravær for team/leder (innenfor modulens regler).",
      adminNote: "Fraværsdata er sensitive – begrens til ledere med legitimt behov.",
    },
    en: {
      includes: "Overview of team absences for managers (within module rules).",
      adminNote: "Absence data is sensitive—limit to managers with legitimate need.",
    },
    sv: {
      includes: "Överblick över frånvaro för team/chef (inom modulens regler).",
      adminNote: "Frånvarodata är känslig—begränsa till chefer med legitimt behov.",
    },
  },
  absence: {
    nb: {
      includes: "Forenklet fraværstilgang (ofte egen bruker eller begrenset oversikt).",
      adminNote: "Avklar overlapp mot «absences» og «hours_register».",
    },
    en: {
      includes: "Simplified absence access (often self or limited view).",
      adminNote: "Clarify overlap with absences and hours_register roles.",
    },
    sv: {
      includes: "Förenklad frånvaroåtkomst (ofta egen eller begränsad vy).",
      adminNote: "Klargör överlapp mot «absences» och «hours_register».",
    },
  },
  absence_settings: {
    nb: {
      includes: "Endre fraværstyper, kvoter og regler.",
      adminNote: "Feil type gir feil lønn/ferie – kun HR eller administrator.",
    },
    en: {
      includes: "Change absence types, quotas and rules.",
      adminNote: "Wrong types break payroll/leave—HR or admin only.",
    },
    sv: {
      includes: "Ändra frånvarotyper, kvoter och regler.",
      adminNote: "Fel typ påverkar lön/semester—endast HR eller admin.",
    },
  },
  meeting_minutes: {
    nb: {
      includes: "Møtereferater: opprette, dele og arkivere møtenotater.",
      adminNote: "Kan inneholde beslutninger med juridisk betydning – lås versjon etter signering.",
    },
    en: {
      includes: "Meeting minutes: create, share and archive notes.",
      adminNote: "May contain legally relevant decisions—lock versions after sign-off.",
    },
    sv: {
      includes: "Mötesprotokoll: skapa, dela och arkivera.",
      adminNote: "Kan innehålla juridiskt relevanta beslut—lås version efter signering.",
    },
  },
  meeting_minutes_templates: {
    nb: {
      includes: "Administrere maler for møtereferat.",
      adminNote: "Malendringer påvirker alle møter – versjon og informer sekretærer.",
    },
    en: {
      includes: "Administer meeting minute templates.",
      adminNote: "Template changes hit all meetings—version and notify secretaries.",
    },
    sv: {
      includes: "Administrera mallar för mötesprotokoll.",
      adminNote: "Malländringar påverkar alla möten—versionera och informera sekreterare.",
    },
  },
  drawings: {
    nb: {
      includes: "Tegninger og teknisk dokumentasjon knyttet til prosjekter.",
      adminNote: "Ofte immaterielle rettigheter – begrens nedlasting og ekstern deling.",
    },
    en: {
      includes: "Drawings and technical documentation linked to projects.",
      adminNote: "Often IP-sensitive—limit download and external sharing.",
    },
    sv: {
      includes: "Ritningar och teknisk dokumentation kopplad till projekt.",
      adminNote: "Ofta IP-känsligt—begränsa nedladdning och extern delning.",
    },
  },
  accounting: {
    nb: {
      includes: "Regnskapsrelaterte funksjoner og eksport (der modulen finnes).",
      adminNote: "Sterk tilgang til økonomidata. Oftest kun administrator i Promang.",
    },
    en: {
      includes: "Accounting-related functions and exports (where available).",
      adminNote: "Strong access to financial data—usually admin-only in Promang.",
    },
    sv: {
      includes: "Redovisningsrelaterade funktioner och export (där tillgängligt).",
      adminNote: "Stark åtkomst till ekonomidata—oftast endast admin i Promang.",
    },
  },
  payslips: {
    nb: {
      includes: "Se og laste ned egne lønnslipper fra Tripletex (Mine lønnslipper).",
      adminNote:
        "Gir ikke oversikt over andres lønnslipper eller lønnsgrunnlag. For selskapsadministrasjon trengs bedriftsadministrator og «payslips_admin» (Lønn Admin).",
    },
    en: {
      includes: "View and download own Tripletex payslips (My payslips).",
      adminNote:
        "Does not grant company-wide payslips or payroll basis. That requires company admin plus payslips_admin (Payroll Admin).",
    },
    sv: {
      includes: "Visa och ladda ner egna lönespecifikationer från Tripletex.",
      adminNote:
        "Ger inte överblick över andras lönespecifikationer eller löneunderlag. Företagsadmin plus payslips_admin krävs för det.",
    },
  },
  payslips_admin: {
    nb: {
      includes:
        "Alle lønnslipper i selskapet, manuell Tripletex-synk, PDF for andre ansatte og lønnsgrunnlag. Virker bare sammen med bedriftsadministrator-rollen.",
      adminNote:
        "Tildel eksplisitt. Bedriftsadministrator uten denne rollen ser bare egne lønnslipper. Lønn Admin uten admin gir ikke selskapsadministrasjon.",
    },
    en: {
      includes:
        "Company-wide payslips, manual Tripletex sync, other employees’ PDFs, and payroll basis. Only effective together with the company admin role.",
      adminNote:
        "Assign explicitly. Company admin without this role only sees own payslips. Payroll Admin without company admin cannot manage payroll.",
    },
    sv: {
      includes:
        "Alla lönespecifikationer i företaget, manuell Tripletex-synk, PDF för andra anställda och löneunderlag. Gäller bara tillsammans med företagsadmin.",
      adminNote:
        "Tilldela uttryckligen. Företagsadmin utan rollen ser bara egna lönespecifikationer. Lön Admin utan admin ger inte företagsadministration.",
    },
  },
  microsoft_personal_mail_ai: {
    nb: {
      includes: "Tillate AI-funksjoner mot personlig Microsoft-e-post (der aktivert).",
      adminNote: "Krever eksplisitt samtykke og retningslinjer – ikke aktiver for alle uten vurdering.",
    },
    en: {
      includes: "Allow AI features against personal Microsoft mail (where enabled).",
      adminNote: "Requires explicit consent and policy—do not enable broadly without review.",
    },
    sv: {
      includes: "Tillåt AI-funktioner mot personlig Microsoft-e-post (där aktiverat).",
      adminNote: "Kräver uttryckligt samtycke och policy—aktivera inte brett utan genomgång.",
    },
  },
  tasks: {
    nb: {
      includes: "Oppgaver: utføre og avslutte tildelte oppgaver.",
      adminNote: "Eldre/parallell oppgavemodul – avklar om prosjekt- eller saksroller dekker behovet.",
    },
    en: {
      includes: "Tasks: execute and complete assigned tasks.",
      adminNote: "Legacy/parallel task module—check if project or case roles cover the need.",
    },
    sv: {
      includes: "Uppgifter: utföra och slutföra tilldelade uppgifter.",
      adminNote: "Äldre/parallell uppgiftsmodul—kontrollera om projekt- eller ärendenroller räcker.",
    },
  },
  checklists: {
    nb: {
      includes: "Eldre sjekklistetilgang (ofte knyttet til kvalitetsmodul).",
      adminNote: "Vurder «quality_checklists» som hovedrolle for nye oppsett.",
    },
    en: {
      includes: "Legacy checklist access (often tied to the quality module).",
      adminNote: "Prefer «quality_checklists» for new setups.",
    },
    sv: {
      includes: "Äldre checkliståtkomst (ofta kopplad till kvalitetsmodulen).",
      adminNote: "Använd «quality_checklists» som huvudroll för nya upplägg.",
    },
  },
  items: {
    nb: {
      includes: "Varelinjer og forbruk knyttet til prosjekt/ordre (eldre rolle).",
      adminNote: "Avklar mot «products» og «inventory» for nye installasjoner.",
    },
    en: {
      includes: "Item lines and consumption linked to projects/orders (legacy role).",
      adminNote: "Clarify overlap with products and inventory for new installs.",
    },
    sv: {
      includes: "Artikelrader och förbrukning kopplat till projekt/order (äldre roll).",
      adminNote: "Klargör mot «products» och «inventory» vid nya installationer.",
    },
  },
  item_consumption: {
    nb: {
      includes: "Registrere forbruk av varer på oppdrag.",
      adminNote: "Påvirker kost og lager – sørg for rette kostpriser og retningslinjer.",
    },
    en: {
      includes: "Register consumption of goods on jobs.",
      adminNote: "Affects cost and stock—ensure correct cost prices and guidelines.",
    },
    sv: {
      includes: "Registrera varuförbrukning på uppdrag.",
      adminNote: "Påverkar kostnad och lager—säkerställ rätt kostpriser och riktlinjer.",
    },
  },
  sales: {
    nb: {
      includes: "Salgsrolle (reservert/utvidelse i noen installasjoner).",
      adminNote: "Avklar med administrator hva som er aktivert i deres miljø.",
    },
    en: {
      includes: "Sales role (reserved/extension in some installs).",
      adminNote: "Confirm with your admin what is enabled in your environment.",
    },
    sv: {
      includes: "Säljroll (reserverad/utökning i vissa miljöer).",
      adminNote: "Bekräfta med admin vad som är aktiverat hos er.",
    },
  },
  edit_projects: {
    nb: {
      includes: "Eldre navn for redigering av prosjekter (tilsvarer ofte «projects_edit»).",
      adminNote: "Vurder å standardisere på nye rollenavn i brukeradmin.",
    },
    en: {
      includes: "Legacy name for editing projects (often same as projects_edit).",
      adminNote: "Consider standardising on newer role names in user admin.",
    },
    sv: {
      includes: "Äldre namn för projektredigering (motsvarar ofta «projects_edit»).",
      adminNote: "Överväg att standardisera på nyare rollnamn.",
    },
  },
  users: {
    nb: {
      includes: "Administrere brukere og tilganger for selskapet (uten full admin).",
      adminNote: "Gi ikke til eksterne uten avtale. Loggfør endringer i roller ved revisjon.",
    },
    en: {
      includes: "Manage users and access for the company (without full admin).",
      adminNote: "Do not assign to externals without agreement. Keep an audit trail for role changes.",
    },
    sv: {
      includes: "Hantera användare och åtkomst för företaget (utan full admin).",
      adminNote: "Ge inte till externa utan avtal. För revision: spåra rolländringar.",
    },
  },
  company: {
    nb: {
      includes: "Firmainnstillinger som logo, adresse og grunnleggende profil.",
      adminNote: "Endringer kan påvirke fakturaheader og kundevisning – få personer bør endre.",
    },
    en: {
      includes: "Company settings such as logo, address and basic profile.",
      adminNote: "Changes can affect invoice headers and customer-facing views—keep to few editors.",
    },
    sv: {
      includes: "Företagsinställningar som logotyp, adress och grundprofil.",
      adminNote: "Ändringar kan påverka fakturahuvud och kundvy—få personer bör redigera.",
    },
  },
};

/**
 * @param {string} lang i18n language code (nb, no, en, se, sv, pl)
 * @param {string} roleName API role name, e.g. "projects_work-orders_edit"
 * @returns {RoleHelpBlock | null}
 */
export function getCompanyUserRoleHelp(lang, roleName) {
  if (!roleName) return null;
  const loc =
    lang === "sv" || lang === "se"
      ? "sv"
      : lang === "en"
        ? "en"
        : lang === "pl"
          ? "pl"
          : "nb";
  const specific =
    (loc === "pl" ? BY_ROLE_PL[roleName] : BY_ROLE[roleName]?.[loc]) || BY_ROLE[roleName]?.["en"];
  if (specific) return specific;

  const parsed = parseRoleName(roleName);
  if (
    parsed.type === "action" &&
    parsed.action &&
    GENERIC_ACTION_KEYS.has(parsed.action) &&
    GENERIC_ACTION[loc][parsed.action]
  ) {
    return GENERIC_ACTION[loc][parsed.action];
  }
  if (
    parsed.type === "submodule_action" &&
    parsed.action &&
    GENERIC_ACTION_KEYS.has(parsed.action) &&
    GENERIC_ACTION[loc][parsed.action]
  ) {
    return GENERIC_ACTION[loc][parsed.action];
  }
  return null;
}
